import { useCallback, useRef, useState } from 'react';
import { toCanvas } from './copilotCanvas';

/**
 * The pointer plumbing behind a draggable panel, with none of the positioning.
 *
 * Callers get the distance travelled since the press and decide what it means:
 * the developer dock reads it as a screen position, the caption window as an
 * offset from where the dialog centres itself.
 *
 * Pointer capture rather than window listeners, because a panel holding an
 * iframe loses every move the moment the cursor crosses it. The handle element
 * is held in a local: React nulls `currentTarget` once the handler returns, so
 * reading it from inside the listeners leaves nothing to release.
 *
 * @param {(delta: {x: number, y: number}) => void} onMove   called on each move
 * @param {() => void} [onStart]                             called once on press
 * @param {() => void} [onEnd]                               called once on release
 */
export const usePointerDrag = ({ onMove, onStart, onEnd }) => {
  const [dragging, setDragging] = useState(false);
  const moveRef = useRef(onMove);
  const startRef = useRef(onStart);
  const endRef = useRef(onEnd);

  moveRef.current = onMove;
  startRef.current = onStart;
  endRef.current = onEnd;

  const startDrag = useCallback((e) => {
    const handle = e.currentTarget;
    if (!handle?.setPointerCapture) return;

    e.preventDefault();
    handle.setPointerCapture(e.pointerId);
    setDragging(true);
    startRef.current?.();

    const from = { x: e.clientX, y: e.clientY };

    const move = (event) =>
      // Screen pixels to canvas pixels, so the panel keeps up with the cursor under zoom.
      moveRef.current?.({ x: toCanvas(event.clientX - from.x), y: toCanvas(event.clientY - from.y) });

    const finish = (event) => {
      setDragging(false);
      handle.releasePointerCapture?.(event.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', finish);
      handle.removeEventListener('pointercancel', finish);
      endRef.current?.();
    };

    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', finish);
  }, []);

  return { dragging, startDrag };
};
